import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import { 
  Users, 
  BookOpen, 
  DollarSign, 
  AlertTriangle, 
  TrendingUp, 
  UserPlus, 
  UserCheck,
  Calendar,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  BarChart,
  Plus,
  FileText,
  Clock,
  Send,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { mockStudents, mockTutors, mockBillingAnomalies, mockClasses } from '../../mockData/dashboardData';
import { mockTasks, mockTimesheets, mockMonthlyInvoiceData } from '../../mockData/taskData';
import { LineChart, Line, BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import AddTaskModal from '../../components/modals/AddTaskModal';
import AddTimesheetModal from '../../components/modals/AddTimesheetModal';
import GeneratePayoutModal from '../../components/modals/GeneratePayoutModal';
import GeneratePayoutEnhancedModal from '../../components/modals/GeneratePayoutEnhancedModal';
import PayoutDetailsModal from '../../components/modals/PayoutDetailsModal';
import EditPayoutModal from '../../components/modals/EditPayoutModal';
import GenerateInvoiceModal from '../../components/modals/GenerateInvoiceModal';
import InvoiceDetailsModal from '../../components/modals/InvoiceDetailsModal';
import EditInvoiceModal from '../../components/modals/EditInvoiceModal';
import SubjectsManagement from './SubjectsManagement';
import UsersManagement from './UsersManagement';
import ProfileView from '../../components/ProfileView';
import TutorAvailabilityCardView from '../../components/TutorAvailabilityCardView';
import MessagesPage from './MessagesPage';

// ============================================
// EXTRACTED COMPONENTS (outside main component to prevent re-creation)
// ============================================

// Task Management Component - Extracted to prevent input focus loss
const TaskManagement = React.memo(({ 
  tasks, 
  setTasks, 
  taskSearchTerm, 
  handleTaskSearchChange,
  taskModalOpen, 
  setTaskModalOpen,
  editingTask,
  setEditingTask,
  fetchTasks
}) => {
  const { getAccessToken } = useAuth();
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const tasksPerPage = 20;

  const handleAddTask = (newTask) => {
    // Refresh tasks from backend
    fetchTasks();
    setTaskModalOpen(false);
    setEditingTask(null);
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const token = getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        fetchTasks();
        alert('Task deleted successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to delete task: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      alert('Failed to delete task');
    }
  };

  const handleToggleActive = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    
    try {
      const token = getAccessToken();
      const newStatus = task.status === 'active' ? 'inactive' : 'active';
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        }
      );
      
      if (response.ok) {
        fetchTasks();
      } else {
        const error = await response.json();
        alert(`Failed to toggle task: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to toggle task:', error);
      alert('Failed to toggle task');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskModalOpen(true);
  };

  const handleUpdateTask = (updatedTask) => {
    fetchTasks();
    setEditingTask(null);
    setTaskModalOpen(false);
  };

  // Filter tasks by search term
  const filteredTasks = tasks.filter(task =>
    task.title?.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
    task.subject?.toLowerCase().includes(taskSearchTerm.toLowerCase())
  );

  // Filter by active/inactive status
  const statusFilteredTasks = showActiveOnly
    ? filteredTasks.filter(t => t.status === 'active')
    : filteredTasks.filter(t => t.status === 'inactive');
  
  // Calculate pagination
  const totalPages = Math.ceil(statusFilteredTasks.length / tasksPerPage);
  const startIndex = (currentPage - 1) * tasksPerPage;
  const endIndex = startIndex + tasksPerPage;
  const paginatedTasks = statusFilteredTasks.slice(startIndex, endIndex);

  // Reset to page 1 when changing filters
  useEffect(() => {
    setCurrentPage(1);
  }, [showActiveOnly, taskSearchTerm]);

  console.log('Total tasks:', tasks.length);
  console.log('Filtered tasks:', filteredTasks.length);
  console.log('Status filtered tasks:', statusFilteredTasks.length);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Task Management</h2>
        <Button 
          onClick={() => setTaskModalOpen(true)}
          className="bg-cyan-600 hover:bg-cyan-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Task
        </Button>
      </div>

      {/* Search and Toggle Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg space-y-4">
        {/* Search Box */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search tasks by title or subject..."
            className="pl-10"
            value={taskSearchTerm}
            onChange={handleTaskSearchChange}
          />
        </div>

        {/* Active/Inactive Toggle */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowActiveOnly(true)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              showActiveOnly
                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <CheckCircle className="w-4 h-4 inline mr-2" />
            Active Tasks ({filteredTasks.filter(t => t.status === 'active').length})
          </button>
          <button
            onClick={() => setShowActiveOnly(false)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              !showActiveOnly
                ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <XCircle className="w-4 h-4 inline mr-2" />
            Inactive Tasks ({filteredTasks.filter(t => t.status === 'inactive').length})
          </button>
        </div>
      </div>

      {/* Tasks Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
          {showActiveOnly ? 'Active' : 'Inactive'} Tasks ({statusFilteredTasks.length})
        </h3>
        <div className="space-y-4">
          {paginatedTasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No {showActiveOnly ? 'active' : 'inactive'} tasks found
            </div>
          ) : (
            paginatedTasks.map((task) => (
            <div key={task.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-cyan-300 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg">{task.title}</h4>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300">
                      {task.subject}
                    </span>
                  </div>
                  {task.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{task.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
                    {task.start_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Started: {new Date(task.start_date).toLocaleDateString()}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      Billing: ₹{task.billing_rate}/hr
                    </span>
                    <span className="flex items-center gap-1">
                      Payout: ₹{task.payout_rate}/hr
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleEditTask(task)}
                    className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-lg transition-colors"
                    title="Edit Task"
                  >
                    <Edit className="w-4 h-4 text-blue-600" />
                  </button>
                  <button 
                    onClick={() => handleToggleActive(task.id)}
                    className="p-2 hover:bg-orange-50 dark:hover:bg-orange-900 rounded-lg transition-colors"
                    title="Make Inactive"
                  >
                    <XCircle className="w-4 h-4 text-orange-600" />
                  </button>
                  <button 
                    onClick={() => handleDeleteTask(task.id)}
                    className="p-2 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg transition-colors"
                    title="Delete Task"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ))
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {startIndex + 1} to {Math.min(endIndex, statusFilteredTasks.length)} of {statusFilteredTasks.length} tasks
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <AddTaskModal 
        isOpen={taskModalOpen}
        onClose={() => {
          setTaskModalOpen(false);
          setEditingTask(null);
        }}
        onSubmit={editingTask ? handleUpdateTask : handleAddTask}
        editingTask={editingTask}
      />
    </div>
  );
});

// Messages View Component - Extracted to prevent input focus loss
const MessagesView = React.memo(({ messageText, handleMessageTextChange }) => {
  const mockMessages = [
    {
      id: 1,
      from: 'Mrs. Kumar',
      role: 'Parent',
      message: 'Can you provide a detailed progress report for my son Rahul?',
      timestamp: '1 hour ago',
      unread: true
    },
    {
      id: 2,
      from: 'Dr. Priya Sharma',
      role: 'Tutor',
      message: 'I need clarification on the timesheet approval for last week.',
      timestamp: '3 hours ago',
      unread: true
    },
    {
      id: 3,
      from: 'Ananya Patel',
      role: 'Student',
      message: 'Thank you for resolving the billing issue!',
      timestamp: '1 day ago',
      unread: false
    }
  ];

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Messages List */}
      <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Messages</h3>
        <div className="space-y-3">
          {mockMessages.map((msg) => (
            <div 
              key={msg.id}
              className={`p-3 rounded-xl cursor-pointer transition-colors ${
                msg.unread 
                  ? 'bg-cyan-50 dark:bg-cyan-900 border-l-4 border-cyan-500' 
                  : 'bg-gray-50 dark:bg-gray-700'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm">{msg.from}</p>
                    {msg.unread && <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{msg.role}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{msg.message}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{msg.timestamp}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Message Thread */}
      <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-lg flex flex-col" style={{ height: '600px' }}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">Mrs. Kumar</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Parent</p>
            </div>
          </div>
        </div>

        <div className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-tl-none p-3">
                  <p className="text-sm text-gray-900 dark:text-white">Can you provide a detailed progress report for my son Rahul?</p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">1 hour ago</p>
              </div>
            </div>

            <div className="flex items-start gap-3 justify-end">
              <div className="flex-1 flex flex-col items-end">
                <div className="bg-cyan-600 rounded-2xl rounded-tr-none p-3 max-w-md">
                  <p className="text-sm text-white">Of course! I'll prepare a comprehensive report and send it to you by end of day.</p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">30 minutes ago</p>
              </div>
              <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Message Input */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <Input
              placeholder="Type your message..."
              value={messageText}
              onChange={handleMessageTextChange}
              className="flex-1"
            />
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

const AdminDashboard = () => {
  const { user, getAccessToken } = useAuth();
  const [activeView, setActiveView] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [parents, setParents] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  const [tutorSearchTerm, setTutorSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('January');
  const [selectedYear, setSelectedYear] = useState('2025');
  const [editingTask, setEditingTask] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingPayout, setEditingPayout] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [generatePayoutModalOpen, setGeneratePayoutModalOpen] = useState(false);
  const [generatePayoutEnhancedModalOpen, setGeneratePayoutEnhancedModalOpen] = useState(false);
  const [payoutDetailsModalOpen, setPayoutDetailsModalOpen] = useState(false);
  const [editPayoutModalOpen, setEditPayoutModalOpen] = useState(false);
  const [selectedPayoutDetails, setSelectedPayoutDetails] = useState(null);
  const [timesheetStats, setTimesheetStats] = useState({ unapproved: 0, total: 0, approved: 0 });
  const [payoutPage, setPayoutPage] = useState(1);
  const [payoutLimit, setPayoutLimit] = useState(20);
  const [payoutTotal, setPayoutTotal] = useState(0);
  
  // Invoice Management State
  const [invoices, setInvoices] = useState([]);
  const [generateInvoiceModalOpen, setGenerateInvoiceModalOpen] = useState(false);
  const [invoiceDetailsModalOpen, setInvoiceDetailsModalOpen] = useState(false);
  const [editInvoiceModalOpen, setEditInvoiceModalOpen] = useState(false);
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
  const [editingInvoiceData, setEditingInvoiceData] = useState(null);
  const [parentSearchTerm, setParentSearchTerm] = useState('');
  const [selectedParentId, setSelectedParentId] = useState(''); // For server-side filtering
  const [invoiceFilterMonth, setInvoiceFilterMonth] = useState('');
  const [invoiceFilterYear, setInvoiceFilterYear] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceLimit, setInvoiceLimit] = useState(20);
  const [invoiceTotal, setInvoiceTotal] = useState(0);

  // Tutor Availability State
  const [selectedTutorForAvailability, setSelectedTutorForAvailability] = useState(null);
  const [availabilityDropdownOpen, setAvailabilityDropdownOpen] = useState(false);
  
  // Analytics State
  const [analyticsData, setAnalyticsData] = useState({
    monthly_revenue: [],
    monthly_payout: [],
    monthly_profit: [],
    revenue_trend: [],
    recent_activity: [],
    totals: { revenue: 0, payout: 0, profit: 0 }
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  
  // Fetch tasks and users from API
  useEffect(() => {
    const loadData = async () => {
      await fetchTasks();
      await fetchTimesheets();
      await fetchPayouts();
      await fetchUsers();
      await fetchTimesheetStats();
      await fetchInvoices();
      await fetchAnalytics();
    };
    loadData();
  }, []);
  
  const fetchTasks = async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        console.log('No token available for fetching tasks');
        return;
      }
      
      console.log('Fetching tasks from backend...');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      console.log('Tasks response status:', response.status);
      
      if (response.ok) {
        const tasksData = await response.json();
        console.log('Tasks fetched successfully:', tasksData);
        console.log('Number of tasks:', tasksData.length);
        setTasks(tasksData);
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch tasks:', errorData);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };
  
  const fetchTimesheets = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/timesheets?page=1&page_size=1000`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Handle both old and new response formats
        const timesheetsData = data.timesheets || data;
        setTimesheets(timesheetsData);
      }
    } catch (error) {
      console.error('Failed to fetch timesheets:', error);
    }
  };
  
  const fetchPayouts = async (page = payoutPage, limit = payoutLimit) => {
    try {
      const token = getAccessToken();
      if (!token) return;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payouts?page=${page}&limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPayouts(data.payouts || []);
        setPayoutTotal(data.total || 0);
        setPayoutPage(data.page || page);
      }
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
    }
  };
  
  const fetchTimesheetStats = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/timesheets/stats/counts`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const stats = await response.json();
        setTimesheetStats(stats);
      }
    } catch (error) {
      console.error('Failed to fetch timesheet stats:', error);
    }
  };

  
  const fetchInvoices = async (
    page = invoicePage, 
    limit = invoiceLimit, 
    statusFilter = invoiceStatusFilter,
    parentId = null,
    month = null,
    year = null
  ) => {
    try {
      const token = getAccessToken();
      if (!token) return;
      
      // Build query parameters with server-side filtering
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/invoices?page=${page}&limit=${limit}`;
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (parentId) {
        url += `&parent_id=${parentId}`;
      }
      if (month) {
        url += `&month=${month}`;
      }
      if (year) {
        url += `&year=${year}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
        setInvoiceTotal(data.total || 0);
        setInvoicePage(data.page || page);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  
  const fetchUsers = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;
      
      // Fetch all users
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const usersData = await response.json();
        console.log('Fetched users:', usersData);
        
        // Separate by role
        setStudents(usersData.filter(u => u.role === 'student'));
        setTutors(usersData.filter(u => u.role === 'tutor'));
        setParents(usersData.filter(u => u.role === 'parent'));
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };
  
  const fetchAnalytics = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;
      
      setAnalyticsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/analytics/dashboard`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };
  
  const pendingAnomalies = mockBillingAnomalies.filter(a => a.status === 'pending');

  const stats = [
    { label: 'Total Students', value: students.length, icon: Users, color: 'from-blue-400 to-cyan-500', change: `${students.length} enrolled` },
    { label: 'Active Tutors', value: tutors.length, icon: UserPlus, color: 'from-green-400 to-emerald-500', change: `${tutors.length} teaching` },
    { label: 'Total Revenue', value: `₹${analyticsData.totals.revenue.toLocaleString()}`, icon: DollarSign, color: 'from-yellow-400 to-orange-500', change: `Profit: ₹${analyticsData.totals.profit.toLocaleString()}` },
    { label: 'Billing Alerts', value: pendingAnomalies.length, icon: AlertTriangle, color: 'from-red-400 to-pink-500', change: 'Needs attention' }
  ];

  // Helper function to get icon component for recent activity
  const getActivityIcon = (iconType, colorType) => {
    const colorClasses = {
      green: 'bg-green-100 text-green-600',
      blue: 'bg-blue-100 text-blue-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600'
    };
    const bgClass = colorClasses[colorType] || 'bg-gray-100 text-gray-600';
    
    const icons = {
      user_check: <UserCheck className="w-5 h-5" />,
      check_circle: <CheckCircle className="w-5 h-5" />,
      calendar: <Calendar className="w-5 h-5" />,
      clock: <Clock className="w-5 h-5" />
    };
    
    return (
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${bgClass}`}>
        {icons[iconType] || <CheckCircle className="w-5 h-5" />}
      </div>
    );
  };

  // Stable callback handlers to prevent input re-mounting
  const handleTaskSearchChange = useCallback((e) => {
    setTaskSearchTerm(e.target.value);
  }, []);

  const handleTutorSearchChange = useCallback((e) => {
    setTutorSearchTerm(e.target.value);
  }, []);

  const handleMessageTextChange = useCallback((e) => {
    setMessageText(e.target.value);
  }, []);

  // Overview Content
  const OverviewContent = () => (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Billing Anomalies */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          AI Billing Anomalies
        </h2>
        {pendingAnomalies.length > 0 ? (
          <div className="space-y-4">
            {pendingAnomalies.map((anomaly) => (
              <div key={anomaly.id} className="border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-gray-700 rounded-xl p-4">
                <div className="flex items-start justify-between mb-2">
                  <span className="px-2 py-1 bg-red-200 text-red-800 text-xs font-semibold rounded-full uppercase">{anomaly.severity}</span>
                  <span className="text-sm font-bold text-red-600">₹{anomaly.amount}</span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{anomaly.description}</p>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Resolve
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1">Review</Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center py-8 text-gray-500 dark:text-gray-400">No anomalies detected</p>
        )}
      </div>

      {/* Recent Activity - Now from Database */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Recent Activity</h2>
        <div className="space-y-4 max-h-80 overflow-y-auto">
          {analyticsLoading ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto mb-2"></div>
              Loading activity...
            </div>
          ) : analyticsData.recent_activity.length > 0 ? (
            analyticsData.recent_activity.map((activity, index) => (
              <div key={index} className="flex items-start gap-3 pb-4 border-b border-gray-100 dark:border-gray-700 last:border-0">
                {getActivityIcon(activity.icon, activity.color)}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{activity.title}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{activity.description}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{activity.time_ago}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center py-8 text-gray-500 dark:text-gray-400">No recent activity</p>
          )}
        </div>
      </div>

      {/* Revenue Trend Line Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg lg:col-span-2">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Revenue Trend</h2>
        {analyticsLoading ? (
          <div className="h-64 flex items-center justify-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mr-2"></div>
            Loading chart...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analyticsData.revenue_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']} />
              <Line type="monotone" dataKey="revenue" stroke="#0891b2" strokeWidth={3} dot={{ fill: '#0891b2', r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Monthly Revenue Bar Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-green-600" />
          Monthly Revenue
        </h2>
        {analyticsLoading ? (
          <div className="h-64 flex items-center justify-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mr-2"></div>
            Loading...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <RechartsBarChart data={analyticsData.monthly_revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']} />
              <Bar dataKey="amount" fill="#10b981" radius={[4, 4, 0, 0]} />
            </RechartsBarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Monthly Payout Bar Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-blue-600" />
          Monthly Payout
        </h2>
        {analyticsLoading ? (
          <div className="h-64 flex items-center justify-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-2"></div>
            Loading...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <RechartsBarChart data={analyticsData.monthly_payout}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Payout']} />
              <Bar dataKey="amount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </RechartsBarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Monthly Profit Bar Chart (Revenue - Payout) */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg lg:col-span-2">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <BarChart className="w-6 h-6 text-purple-600" />
          Monthly Profit (Revenue - Payout)
        </h2>
        {analyticsLoading ? (
          <div className="h-64 flex items-center justify-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-2"></div>
            Loading...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <RechartsBarChart data={analyticsData.monthly_profit}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Profit']} />
              <Bar dataKey="amount" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </RechartsBarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );


  // Timesheet Management View
  const TimesheetManagement = () => {
    const [timesheetModalOpen, setTimesheetModalOpen] = useState(false);
    const [timesheetFilters, setTimesheetFilters] = useState({
      month: '',
      year: '',
      task_id: ''
    });
    const [currentTimesheetPage, setCurrentTimesheetPage] = useState(1);
    const [timesheetData, setTimesheetData] = useState({
      timesheets: [],
      total: 0,
      page: 1,
      page_size: 20
    });
    const [selectedTimesheets, setSelectedTimesheets] = useState([]);
    const [isApproving, setIsApproving] = useState(false);

    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const years = ['2023', '2024', '2025'];

    // Fetch filtered timesheets
    useEffect(() => {
      fetchFilteredTimesheets();
    }, [timesheetFilters, currentTimesheetPage]);

    // Clear selections when data changes
    useEffect(() => {
      setSelectedTimesheets([]);
    }, [timesheetData.timesheets]);

    const fetchFilteredTimesheets = async () => {
      try {
        const token = getAccessToken();
        if (!token) return;
        
        const params = new URLSearchParams({
          page: currentTimesheetPage,
          page_size: 20
        });
        
        if (timesheetFilters.month) params.append('month', timesheetFilters.month);
        if (timesheetFilters.year) params.append('year', timesheetFilters.year);
        if (timesheetFilters.task_id) params.append('task_id', timesheetFilters.task_id);
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/timesheets?${params}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setTimesheetData(data);
        }
      } catch (error) {
        console.error('Failed to fetch timesheets:', error);
      }
    };

    const handleDeleteTimesheet = async (timesheetId) => {
      if (!window.confirm('Are you sure you want to delete this timesheet?')) return;
      
      try {
        const token = getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/timesheets/${timesheetId}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          fetchFilteredTimesheets();
          alert('Timesheet deleted successfully!');
        } else {
          const error = await response.json();
          alert(`Failed to delete timesheet: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to delete timesheet:', error);
        alert('Failed to delete timesheet');
      }
    };

    const handleSelectAll = () => {
      // Only select non-approved timesheets
      const nonApprovedTimesheets = timesheetData.timesheets.filter(ts => ts.status !== 'approved');
      if (selectedTimesheets.length === nonApprovedTimesheets.length) {
        setSelectedTimesheets([]);
      } else {
        setSelectedTimesheets(nonApprovedTimesheets.map(ts => ts.id));
      }
    };

    const handleSelectTimesheet = (timesheetId) => {
      setSelectedTimesheets(prev => {
        if (prev.includes(timesheetId)) {
          return prev.filter(id => id !== timesheetId);
        } else {
          return [...prev, timesheetId];
        }
      });
    };

    const handleBulkApprove = async () => {
      if (selectedTimesheets.length === 0) {
        alert('Please select at least one timesheet to approve');
        return;
      }

      if (!window.confirm(`Are you sure you want to approve ${selectedTimesheets.length} timesheet(s)?`)) {
        return;
      }

      setIsApproving(true);
      
      try {
        const token = getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/timesheets/bulk-approve`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(selectedTimesheets)
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          alert(result.message);
          setSelectedTimesheets([]);
          fetchFilteredTimesheets();
          fetchTimesheetStats(); // Refresh stats after approval
        } else {
          const error = await response.json();
          alert(`Failed to approve timesheets: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to approve timesheets:', error);
        alert('Failed to approve timesheets');
      } finally {
        setIsApproving(false);
      }
    };

    const totalPages = Math.ceil(timesheetData.total / timesheetData.page_size);
    const nonApprovedTimesheets = timesheetData.timesheets.filter(ts => ts.status !== 'approved');
    const allNonApprovedSelected = nonApprovedTimesheets.length > 0 && selectedTimesheets.length === nonApprovedTimesheets.length;

    return (
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Filters</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Month</label>
              <select
                value={timesheetFilters.month}
                onChange={(e) => {
                  setTimesheetFilters({...timesheetFilters, month: e.target.value});
                  setCurrentTimesheetPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Months</option>
                {months.map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Year</label>
              <select
                value={timesheetFilters.year}
                onChange={(e) => {
                  setTimesheetFilters({...timesheetFilters, year: e.target.value});
                  setCurrentTimesheetPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Years</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Task</label>
              <select
                value={timesheetFilters.task_id}
                onChange={(e) => {
                  setTimesheetFilters({...timesheetFilters, task_id: e.target.value});
                  setCurrentTimesheetPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Tasks</option>
                {tasks.map(task => (
                  <option key={task.id} value={task.id}>{task.title}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">All Timesheets ({timesheetData.total})</h2>
              <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                {timesheetStats.unapproved} unapproved entries
              </p>
            </div>
            <div className="flex items-center gap-3">
              {selectedTimesheets.length > 0 && (
                <Button 
                  onClick={handleBulkApprove}
                  disabled={isApproving}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {isApproving ? 'Approving...' : `Approve Selected (${selectedTimesheets.length})`}
                </Button>
              )}
              <Button 
                onClick={() => setTimesheetModalOpen(true)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Timesheet
              </Button>
            </div>
          </div>
          
          {timesheetData.timesheets.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No timesheets found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-center py-3 px-4 w-12">
                        <input
                          type="checkbox"
                          checked={allNonApprovedSelected}
                          onChange={handleSelectAll}
                          disabled={nonApprovedTimesheets.length === 0}
                          className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500 cursor-pointer"
                          title={allNonApprovedSelected ? "Deselect all" : "Select all non-approved"}
                        />
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Date</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Task</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Description</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Hours</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Bill Amount</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Payout Amount</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Status</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timesheetData.timesheets.map((timesheet) => {
                      const isApproved = timesheet.status === 'approved';
                      const isSelected = selectedTimesheets.includes(timesheet.id);
                      
                      return (
                        <tr 
                          key={timesheet.id} 
                          className={`border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                            isSelected ? 'bg-cyan-50 dark:bg-cyan-900/20' : ''
                          }`}
                        >
                          <td className="py-3 px-4 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectTimesheet(timesheet.id)}
                              disabled={isApproved}
                              className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            />
                          </td>
                          <td className="py-3 px-4">{new Date(timesheet.work_date).toLocaleDateString()}</td>
                          <td className="py-3 px-4">
                            {timesheet.task_name ? (
                              <div className="font-semibold text-gray-900 dark:text-white">{timesheet.task_name}</div>
                            ) : (
                              <span className="text-gray-400">Unknown Task</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{timesheet.description || '-'}</td>
                          <td className="py-3 px-4 text-right font-semibold">
                            {timesheet.hours_worked.toFixed(1)}h
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-blue-600 dark:text-blue-400">
                            ₹{(timesheet.bill_amount || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-green-600 dark:text-green-400">
                            ₹{(timesheet.payout_amount || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                              isApproved 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                : timesheet.status === 'submitted'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                : timesheet.status === 'rejected'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {timesheet.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button 
                                onClick={() => handleDeleteTimesheet(timesheet.id)}
                                className="p-2 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg transition-colors"
                                title="Delete timesheet"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Showing {((currentTimesheetPage - 1) * timesheetData.page_size) + 1} to {Math.min(currentTimesheetPage * timesheetData.page_size, timesheetData.total)} of {timesheetData.total} timesheets
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => setCurrentTimesheetPage(p => Math.max(1, p - 1))}
                      disabled={currentTimesheetPage === 1}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Page {currentTimesheetPage} of {totalPages}
                    </span>
                    <Button
                      onClick={() => setCurrentTimesheetPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentTimesheetPage === totalPages}
                      variant="outline"
                      size="sm"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <AddTimesheetModal 
          isOpen={timesheetModalOpen}
          onClose={() => setTimesheetModalOpen(false)}
          onSubmit={() => {
            fetchFilteredTimesheets();
            setTimesheetModalOpen(false);
          }}
        />
      </div>
    );
  };

  // Payout Management View
  const PayoutManagement = () => {
    const handleGeneratePayout = async (payoutData) => {
      try {
        const token = getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payouts/generate`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payoutData)
          }
        );
        
        if (response.ok) {
          await fetchPayouts();
          setGeneratePayoutModalOpen(false);
          alert('Payout generated successfully!');
        } else {
          const error = await response.json();
          alert(`Failed to generate payout: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to generate payout:', error);
        alert('Failed to generate payout');
      }
    };


    const handleGeneratePayoutEnhanced = async (payoutData, progressCallback) => {
      try {
        const token = getAccessToken();
        
        // Call the bulk generation endpoint
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payouts/generate-bulk`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payoutData)
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          
          // Update progress (simulate completion)
          if (progressCallback) {
            progressCallback(result.results.length, result.results.length);
          }
          
          // Refresh payouts list
          await fetchPayouts();
          await fetchTimesheetStats();
          setGeneratePayoutEnhancedModalOpen(false);
          
          // Show detailed results
          const successMsg = result.success_count > 0 ? `${result.success_count} created` : '';
          const updatedMsg = result.updated_count > 0 ? `${result.updated_count} updated` : '';
          const failedMsg = result.failed_count > 0 ? `${result.failed_count} failed` : '';
          const parts = [successMsg, updatedMsg, failedMsg].filter(p => p);
          
          alert(`Payout generation completed!\n${parts.join(', ')}\n\nDetails:\n${result.results.map(r => `${r.tutor_name}: ${r.status} - ${r.message}`).join('\n')}`);
        } else {
          const error = await response.json();
          alert(`Failed to generate payouts: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to generate payouts:', error);
        alert('Failed to generate payouts');
      }
    };


    const handleViewPayoutDetails = async (payoutId) => {
      try {
        const token = getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payouts/${payoutId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          const details = await response.json();
          setSelectedPayoutDetails(details);
          setPayoutDetailsModalOpen(true);
        } else {
          const error = await response.json();
          alert(`Failed to fetch payout details: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to fetch payout details:', error);
        alert('Failed to fetch payout details');
      }
    };

    const handleUpdatePayout = async (payoutId, updates) => {
      try {
        const token = getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payouts/${payoutId}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updates)
          }
        );
        
        if (response.ok) {
          await fetchPayouts();
          setEditPayoutModalOpen(false);
          setEditingPayout(null);
          alert('Payout updated successfully!');
        } else {
          const error = await response.json();
          alert(`Failed to update payout: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to update payout:', error);
        alert('Failed to update payout');
      }
    };

    // Note: Filtering is now handled on the backend with pagination
    // Frontend filters are displayed but not actively filtering in this version
    const filteredPayouts = payouts;

    // Calculate stats
    const totalAmount = payouts.reduce((sum, p) => sum + p.final_amount, 0);
    const totalPayouts = payoutTotal;

    return (
      <div className="space-y-6">
        {/* Header with Generate Button */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Payout Management</h2>
          <Button 
            onClick={() => setGeneratePayoutEnhancedModalOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Generate Payouts
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 shadow-lg text-white">
            <p className="text-sm text-green-100 mb-2">Total Payouts Generated</p>
            <p className="text-3xl font-bold mb-1">₹{totalAmount.toLocaleString()}</p>
            <p className="text-xs text-green-100">{totalPayouts} payouts</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 shadow-lg text-white">
            <p className="text-sm text-blue-100 mb-2">Pending Payouts</p>
            <p className="text-3xl font-bold mb-1">{filteredPayouts.filter(p => p.status === 'pending').length}</p>
            <p className="text-xs text-blue-100">Awaiting processing</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 shadow-lg text-white">
            <p className="text-sm text-purple-100 mb-2">Completed Payouts</p>
            <p className="text-3xl font-bold mb-1">{filteredPayouts.filter(p => p.status === 'completed').length}</p>
            <p className="text-xs text-purple-100">Successfully processed</p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Search by Tutor Name</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search tutor..."
              className="pl-10"
              value={tutorSearchTerm}
              onChange={handleTutorSearchChange}
            />
          </div>
        </div>

        {/* Payouts List */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Generated Payouts ({payoutTotal})
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">Items per page:</span>
              <select
                value={payoutLimit}
                onChange={(e) => {
                  setPayoutLimit(Number(e.target.value));
                  setPayoutPage(1);
                  fetchPayouts(1, Number(e.target.value));
                }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {payoutTotal === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No payouts generated yet</p>
              <p className="text-sm mt-2">Click "Generate Monthly Payout" to create a payout</p>
            </div>
          ) : (
            <>
            <div className="space-y-4">
              {filteredPayouts.map((payout) => (
              <div key={payout.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-cyan-300 dark:hover:border-cyan-600 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white text-2xl font-bold">
                      {payout.tutor_name?.charAt(0) || 'T'}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white text-lg">{payout.tutor_name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">#{payout.payout_number}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {new Date(payout.period_start).toLocaleDateString()} - {new Date(payout.period_end).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">₹{payout.final_amount.toLocaleString()}</p>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      payout.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                      payout.status === 'processing' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                      payout.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                      'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    }`}>
                      {payout.status}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Base Amount</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">₹{payout.amount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Adjustments</p>
                    <p className="text-lg font-bold text-blue-600">+₹{payout.adjustments.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Deductions</p>
                    <p className="text-lg font-bold text-red-600">-₹{payout.deductions.toLocaleString()}</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => {
                      setEditingPayout(payout);
                      setEditPayoutModalOpen(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleViewPayoutDetails(payout.id)}
                  >
                    View Details
                  </Button>
                </div>
              </div>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {Math.ceil(payoutTotal / payoutLimit) > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((payoutPage - 1) * payoutLimit) + 1} to {Math.min(payoutPage * payoutLimit, payoutTotal)} of {payoutTotal} payouts
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={() => {
                      const newPage = Math.max(1, payoutPage - 1);
                      setPayoutPage(newPage);
                      fetchPayouts(newPage, payoutLimit);
                    }}
                    disabled={payoutPage === 1}
                    variant="outline"
                    size="sm"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Page {payoutPage} of {Math.ceil(payoutTotal / payoutLimit)}
                  </span>
                  <Button
                    onClick={() => {
                      const newPage = Math.min(Math.ceil(payoutTotal / payoutLimit), payoutPage + 1);
                      setPayoutPage(newPage);
                      fetchPayouts(newPage, payoutLimit);
                    }}
                    disabled={payoutPage === Math.ceil(payoutTotal / payoutLimit)}
                    variant="outline"
                    size="sm"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </div>

        {/* Modals */}
        <GeneratePayoutModal
          isOpen={generatePayoutModalOpen}
          onClose={() => setGeneratePayoutModalOpen(false)}
          onGenerate={handleGeneratePayout}
          tutors={tutors}
        />

        <GeneratePayoutEnhancedModal
          isOpen={generatePayoutEnhancedModalOpen}
          onClose={() => setGeneratePayoutEnhancedModalOpen(false)}
          onGenerate={handleGeneratePayoutEnhanced}
          tutors={tutors}
        />

        <PayoutDetailsModal
          isOpen={payoutDetailsModalOpen}
          onClose={() => {
            setPayoutDetailsModalOpen(false);
            setSelectedPayoutDetails(null);
          }}
          payoutDetails={selectedPayoutDetails}
        />

        <EditPayoutModal
          isOpen={editPayoutModalOpen}
          onClose={() => {
            setEditPayoutModalOpen(false);
            setEditingPayout(null);
          }}
          onUpdate={handleUpdatePayout}
          payout={editingPayout}
        />
      </div>
    );
  };



  // Invoice Management View Component
  const InvoiceManagement = () => {
    const handleGenerateInvoice = async (invoiceData, progressCallback) => {
      try {
        const token = getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/invoices/generate-bulk`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(invoiceData)
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          if (progressCallback) {
            progressCallback(result.results.length, result.results.length);
          }
          
          await fetchInvoices();
          setGenerateInvoiceModalOpen(false);
          
          const successMsg = result.success_count > 0 ? `${result.success_count} created` : '';
          const updatedMsg = result.updated_count > 0 ? `${result.updated_count} updated` : '';
          const failedMsg = result.failed_count > 0 ? `${result.failed_count} failed` : '';
          const parts = [successMsg, updatedMsg, failedMsg].filter(p => p);
          
          alert(`Invoice generation completed!\n${parts.join(', ')}\n\nDetails:\n${result.results.map(r => `${r.parent_name}: ${r.status} - ${r.message}`).join('\n')}`);
        } else {
          const error = await response.json();
          alert(`Failed to generate invoices: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to generate invoices:', error);
        alert('Failed to generate invoices');
      }
    };

    const handleViewInvoiceDetails = async (invoiceId) => {
      try {
        const token = getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          const details = await response.json();
          setSelectedInvoiceDetails(details);
          setInvoiceDetailsModalOpen(true);
        } else {
          const error = await response.json();
          alert(`Failed to fetch invoice details: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to fetch invoice details:', error);
        alert('Failed to fetch invoice details');
      }
    };

    const handleUpdateInvoice = async (invoiceId, updates) => {
      try {
        const token = getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updates)
          }
        );
        
        if (response.ok) {
          await fetchInvoices();
          setEditInvoiceModalOpen(false);
          setEditingInvoiceData(null);
          alert('Invoice updated successfully!');
        } else {
          const error = await response.json();
          alert(`Failed to update invoice: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to update invoice:', error);
        alert('Failed to update invoice');
      }
    };

    const handleSubmitInvoice = async (invoiceId) => {
      if (!window.confirm('Submit this invoice? The parent will receive an email with a Razorpay payment link.')) {
        return;
      }
      
      try {
        const token = getAccessToken();
        
        // Send invoice via Razorpay (this also submits it)
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/razorpay/send-invoice/${invoiceId}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          await fetchInvoices();
          alert(`Invoice submitted and sent via Razorpay successfully! Parent will receive payment link via email.`);
        } else {
          const error = await response.json();
          alert(`Failed to submit invoice: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to submit invoice:', error);
        alert('Failed to submit invoice');
      }
    };

    const handleSendViaRazorpay = async (invoiceId) => {
      if (!window.confirm('Send this invoice via Razorpay? The parent will receive an email with payment link.')) {
        return;
      }
      
      try {
        const token = getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/razorpay/send-invoice/${invoiceId}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          await fetchInvoices();
          alert(`Invoice sent via Razorpay successfully! Payment link: ${result.razorpay_short_url || 'Check email'}`);
        } else {
          const error = await response.json();
          alert(`Failed to send invoice via Razorpay: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to send invoice via Razorpay:', error);
        alert('Failed to send invoice via Razorpay');
      }
    };

    const handleDeleteInvoice = async (invoiceId) => {
      if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
        return;
      }
      
      try {
        const token = getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          await fetchInvoices();
          alert('Invoice deleted successfully!');
        } else {
          const error = await response.json();
          alert(`Failed to delete invoice: ${error.detail}`);
        }
      } catch (error) {
        console.error('Failed to delete invoice:', error);
        alert('Failed to delete invoice');
      }
    };

    // Get unique parents with IDs for dropdown (use the parents state which has all parent users)
    const parentOptions = parents.map(p => ({ id: p.id, name: p.full_name })).sort((a, b) => a.name.localeCompare(b.name));

    // Server-side filtering is now used, so filteredInvoices = invoices
    const filteredInvoices = invoices;

    const totalAmount = invoices.reduce((sum, i) => sum + i.final_amount, 0);
    const totalInvoices = invoiceTotal;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Invoice Management</h2>
          <Button onClick={() => setGenerateInvoiceModalOpen(true)} className="bg-green-600 hover:bg-green-700 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Generate Invoices
          </Button>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 shadow-lg text-white">
            <p className="text-sm text-purple-100 mb-2">Total Invoices</p>
            <p className="text-3xl font-bold mb-1">₹{totalAmount.toLocaleString()}</p>
            <p className="text-xs text-purple-100">{totalInvoices} invoices</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-500 to-orange-600 rounded-2xl p-6 shadow-lg text-white">
            <p className="text-sm text-yellow-100 mb-2">Draft Invoices</p>
            <p className="text-3xl font-bold mb-1">{filteredInvoices.filter(i => i.status === 'draft').length}</p>
            <p className="text-xs text-yellow-100">Not submitted yet</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 shadow-lg text-white">
            <p className="text-sm text-green-100 mb-2">Paid Invoices</p>
            <p className="text-3xl font-bold mb-1">{filteredInvoices.filter(i => i.status === 'paid').length}</p>
            <p className="text-xs text-green-100">Successfully paid</p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Filter Invoices</label>
          
          {/* Status Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => {
                setInvoiceStatusFilter('all');
                setInvoicePage(1);
                fetchInvoices(1, invoiceLimit, 'all', selectedParentId || null, invoiceFilterMonth || null, invoiceFilterYear || null);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                invoiceStatusFilter === 'all'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All Invoices
            </button>
            <button
              onClick={() => {
                setInvoiceStatusFilter('draft');
                setInvoicePage(1);
                fetchInvoices(1, invoiceLimit, 'draft', selectedParentId || null, invoiceFilterMonth || null, invoiceFilterYear || null);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                invoiceStatusFilter === 'draft'
                  ? 'bg-gray-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Draft
            </button>
            <button
              onClick={() => {
                setInvoiceStatusFilter('submitted');
                setInvoicePage(1);
                fetchInvoices(1, invoiceLimit, 'submitted', selectedParentId || null, invoiceFilterMonth || null, invoiceFilterYear || null);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                invoiceStatusFilter === 'submitted'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Submitted
            </button>
            <button
              onClick={() => {
                setInvoiceStatusFilter('paid');
                setInvoicePage(1);
                fetchInvoices(1, invoiceLimit, 'paid', selectedParentId || null, invoiceFilterMonth || null, invoiceFilterYear || null);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                invoiceStatusFilter === 'paid'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Paid
            </button>
            <button
              onClick={() => {
                setInvoiceStatusFilter('overdue');
                setInvoicePage(1);
                fetchInvoices(1, invoiceLimit, 'overdue', selectedParentId || null, invoiceFilterMonth || null, invoiceFilterYear || null);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                invoiceStatusFilter === 'overdue'
                  ? 'bg-red-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Overdue
            </button>
          </div>

          {/* Other Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Parent Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                <select
                  value={selectedParentId}
                  onChange={(e) => {
                    const newParentId = e.target.value;
                    setSelectedParentId(newParentId);
                    setParentSearchTerm(parentOptions.find(p => p.id === newParentId)?.name || '');
                    setInvoicePage(1);
                    fetchInvoices(1, invoiceLimit, invoiceStatusFilter, newParentId || null, invoiceFilterMonth || null, invoiceFilterYear || null);
                  }}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 appearance-none text-sm"
                >
                  <option value="">All Parents</option>
                  {parentOptions.map((parent) => (
                    <option key={parent.id} value={parent.id}>
                      {parent.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Month</label>
              <select
                value={invoiceFilterMonth}
                onChange={(e) => {
                  const newMonth = e.target.value;
                  setInvoiceFilterMonth(newMonth);
                  setInvoicePage(1);
                  fetchInvoices(1, invoiceLimit, invoiceStatusFilter, selectedParentId || null, newMonth || null, invoiceFilterYear || null);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">All Months</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Year</label>
              <select
                value={invoiceFilterYear}
                onChange={(e) => {
                  const newYear = e.target.value;
                  setInvoiceFilterYear(newYear);
                  setInvoicePage(1);
                  fetchInvoices(1, invoiceLimit, invoiceStatusFilter, selectedParentId || null, invoiceFilterMonth || null, newYear || null);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">All Years</option>
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
              </select>
            </div>
          </div>
          {(selectedParentId || invoiceFilterMonth || invoiceFilterYear || invoiceStatusFilter !== 'all') && (
            <button
              onClick={() => {
                setParentSearchTerm('');
                setSelectedParentId('');
                setInvoiceFilterMonth('');
                setInvoiceFilterYear('');
                setInvoiceStatusFilter('all');
                setInvoicePage(1);
                fetchInvoices(1, invoiceLimit, 'all', null, null, null);
              }}
              className="mt-3 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
            >
              <XCircle className="w-4 h-4" />
              Clear All Filters
            </button>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Generated Invoices ({invoiceTotal})</h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">Items per page:</span>
              <select
                value={invoiceLimit}
                onChange={(e) => {
                  const newLimit = Number(e.target.value);
                  setInvoiceLimit(newLimit);
                  setInvoicePage(1);
                  fetchInvoices(1, newLimit, invoiceStatusFilter, selectedParentId || null, invoiceFilterMonth || null, invoiceFilterYear || null);
                }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {invoiceTotal === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No invoices generated yet</p>
            </div>
          ) : (
            <>
            <div className="space-y-4">
              {filteredInvoices.map((invoice) => (
              <div key={invoice.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-cyan-300 dark:hover:border-cyan-600 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center text-white text-2xl font-bold">
                      {invoice.parent_name?.charAt(0) || 'P'}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white text-lg">{invoice.parent_name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">#{invoice.invoice_number}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-purple-600">₹{invoice.final_amount.toLocaleString()}</p>
                    <div className="flex gap-2 justify-end mt-2">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                        invoice.status === 'submitted' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                        invoice.status === 'overdue' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300'
                      }`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                      {invoice.razorpay_invoice_id && (
                        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                          Razorpay
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Subtotal</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">₹{invoice.subtotal.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Platform Charge</p>
                    <p className="text-lg font-bold text-blue-600">₹{invoice.platform_charge.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">GST ({invoice.gst_percentage}%)</p>
                    <p className="text-lg font-bold text-orange-600">₹{invoice.gst_amount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Discount</p>
                    <p className="text-lg font-bold text-red-600">₹{invoice.discount.toLocaleString()}</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <Button size="sm" onClick={() => { setEditingInvoiceData(invoice); setEditInvoiceModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => handleViewInvoiceDetails(invoice.id)}>
                    View Details
                  </Button>
                  {invoice.status === 'draft' && !invoice.razorpay_invoice_id && (
                    <Button size="sm" onClick={() => handleSubmitInvoice(invoice.id)} className="bg-green-600 hover:bg-green-700 text-white">
                      <Send className="w-4 h-4 mr-2" />
                      Submit & Send
                    </Button>
                  )}
                  {invoice.razorpay_short_url && (
                    <Button size="sm" onClick={() => window.open(invoice.razorpay_short_url, '_blank')} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                      View Payment Link
                    </Button>
                  )}
                  <Button size="sm" onClick={() => handleDeleteInvoice(invoice.id)} variant="outline" className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {Math.ceil(invoiceTotal / invoiceLimit) > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((invoicePage - 1) * invoiceLimit) + 1} to {Math.min(invoicePage * invoiceLimit, invoiceTotal)} of {invoiceTotal} invoices
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={() => {
                      const newPage = Math.max(1, invoicePage - 1);
                      setInvoicePage(newPage);
                      fetchInvoices(newPage, invoiceLimit, invoiceStatusFilter, selectedParentId || null, invoiceFilterMonth || null, invoiceFilterYear || null);
                    }}
                    disabled={invoicePage === 1}
                    variant="outline"
                    size="sm"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Page {invoicePage} of {Math.ceil(invoiceTotal / invoiceLimit)}
                  </span>
                  <Button
                    onClick={() => {
                      const newPage = Math.min(Math.ceil(invoiceTotal / invoiceLimit), invoicePage + 1);
                      setInvoicePage(newPage);
                      fetchInvoices(newPage, invoiceLimit, invoiceStatusFilter, selectedParentId || null, invoiceFilterMonth || null, invoiceFilterYear || null);
                    }}
                    disabled={invoicePage === Math.ceil(invoiceTotal / invoiceLimit)}
                    variant="outline"
                    size="sm"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </div>

        <GenerateInvoiceModal
          isOpen={generateInvoiceModalOpen}
          onClose={() => setGenerateInvoiceModalOpen(false)}
          onGenerate={handleGenerateInvoice}
          parents={parents}
        />

        <InvoiceDetailsModal
          isOpen={invoiceDetailsModalOpen}
          onClose={() => {
            setInvoiceDetailsModalOpen(false);
            setSelectedInvoiceDetails(null);
          }}
          invoiceDetails={selectedInvoiceDetails}
        />

        <EditInvoiceModal
          isOpen={editInvoiceModalOpen}
          onClose={() => {
            setEditInvoiceModalOpen(false);
            setEditingInvoiceData(null);
          }}
          onUpdate={handleUpdateInvoice}
          invoice={editingInvoiceData}
        />
      </div>
    );
  };

  // User Management View
  const UserManagement = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h2>
        <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
          <UserPlus className="w-4 h-4 mr-2" />
          Add New User
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Students Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Students ({students.length})</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Search students..." 
                className="pl-10 w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-3">
            {students.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No students found</p>
              </div>
            ) : (
              students.map((student) => (
                <div key={student.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-cyan-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-cyan-100 dark:bg-cyan-900 flex items-center justify-center">
                        <User className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 dark:text-white">{student.full_name || 'Unnamed Student'}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{student.email}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Role: {student.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs rounded-full">
                        Active
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Tutors Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Tutors ({tutors.length})</h3>
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">
              Verify Tutor
            </Button>
          </div>
          <div className="space-y-3">
            {tutors.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <UserPlus className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No tutors found</p>
              </div>
            ) : (
              tutors.map((tutor) => (
                <div key={tutor.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-green-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                        <UserCheck className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 dark:text-white">{tutor.full_name || 'Unnamed Tutor'}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{tutor.email}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Role: {tutor.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs rounded-full">
                        Verified
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Class Management View
  const ClassManagement = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Class Management</h2>
        <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
          <Calendar className="w-4 h-4 mr-2" />
          Schedule New Class
        </Button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <div className="space-y-4">
          {mockClasses.map((cls) => (
            <div key={cls.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-cyan-300 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-lg">{cls.subject}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Tutor: {cls.tutor}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Student: {cls.student}</p>
                </div>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  cls.status === 'scheduled' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                }`}>
                  {cls.status}
                </span>
              </div>
              <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400 mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{cls.schedule}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{cls.duration} min</span>
                </div>
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">{cls.grade}</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button size="sm" variant="outline" className="flex-1 text-red-600 border-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button size="sm" className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white">
                  View Details
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Billing Management View
  const BillingManagement = () => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const years = ['2023', '2024', '2025'];

    // Use real invoices from the backend
    const filteredInvoices = invoices;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Billing & Invoices</h2>
          <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
            Generate Invoice
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 shadow-lg text-white">
            <p className="text-sm text-green-100 mb-2">Total Revenue</p>
            <p className="text-3xl font-bold mb-1">₹{analyticsData.totals.revenue.toLocaleString()}</p>
            <p className="text-xs text-green-100">Paid invoices</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-6 shadow-lg text-white">
            <p className="text-sm text-orange-100 mb-2">Total Payout</p>
            <p className="text-3xl font-bold mb-1">₹{analyticsData.totals.payout.toLocaleString()}</p>
            <p className="text-xs text-orange-100">Tutor payments</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 shadow-lg text-white">
            <p className="text-sm text-purple-100 mb-2">Total Profit</p>
            <p className="text-3xl font-bold mb-1">₹{analyticsData.totals.profit.toLocaleString()}</p>
            <p className="text-xs text-purple-100">Revenue - Payout</p>
          </div>
        </div>

        {/* Month and Year Filter */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Filter by Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Months</option>
                {months.map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Filter by Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Years</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="pt-7">
              <Button
                onClick={() => {
                  setSelectedMonth('');
                  setSelectedYear('');
                }}
                variant="outline"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            All Invoices {filteredInvoices.length > 0 && `(${filteredInvoices.length})`}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Invoice ID</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Parent</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Student</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Month</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Sessions</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Amount</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Discount</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Adjustment</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Final</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Status</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="py-3 px-4 font-mono text-sm">{invoice.id}</td>
                    <td className="py-3 px-4">{invoice.parent}</td>
                    <td className="py-3 px-4">{invoice.student}</td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{invoice.month}</td>
                    <td className="py-3 px-4 text-right">{invoice.sessions}</td>
                    <td className="py-3 px-4 text-right">₹{invoice.amount}</td>
                    <td className="py-3 px-4 text-right text-red-600">-₹{invoice.discount}</td>
                    <td className="py-3 px-4 text-right text-blue-600">{invoice.adjustment || 0 > 0 ? '+' : ''}₹{invoice.adjustment || 0}</td>
                    <td className="py-3 px-4 text-right font-bold">₹{invoice.finalAmount}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => setEditingInvoice(invoice)}
                          className="p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900 rounded transition-colors"
                          title="Edit Invoice"
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </button>
                        <button 
                          className="p-1.5 hover:bg-green-50 dark:hover:bg-green-900 rounded transition-colors"
                          title="Add Adjustment"
                        >
                          <Plus className="w-4 h-4 text-green-600" />
                        </button>
                        <button className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredInvoices.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No invoices found for the selected period
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Analytics View
  const AnalyticsView = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Platform Analytics</h2>
      
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Revenue Trend</h3>
          {analyticsLoading ? (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mr-2"></div>
              Loading...
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analyticsData.revenue_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" />
                <YAxis stroke="#6b7280" tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']} />
                <Line type="monotone" dataKey="revenue" stroke="#0891b2" strokeWidth={3} dot={{ fill: '#0891b2', r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Monthly Profit</h3>
          {analyticsLoading ? (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-2"></div>
              Loading...
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBarChart data={analyticsData.monthly_profit}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" />
                <YAxis stroke="#6b7280" tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Profit']} />
                <Bar dataKey="amount" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
              </RechartsBarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );

  // Duplicate components removed - using extracted components at top of file

  return (
    <DashboardLayout role="admin" activeView={activeView} onViewChange={setActiveView}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {activeView === 'overview' && 'Admin Dashboard'}
            {activeView === 'tasks' && 'Task Management'}
            {activeView === 'timesheets' && 'Timesheet Management'}
            {activeView === 'billing' && 'Billing Management'}
            {activeView === 'payout' && 'Payout Management'}
            {activeView === 'users' && 'Users Management'}
            {activeView === 'subjects' && 'Subjects Management'}
            {activeView === 'availability' && 'Tutor Availability'}
            {activeView === 'profile' && 'My Profile'}
            {activeView === 'messages' && 'Messages'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {activeView === 'overview' && 'Platform overview and analytics'}
            {activeView === 'tasks' && 'Create and manage tutor-student assignments'}
            {activeView === 'timesheets' && 'Review and approve tutor timesheets'}
            {activeView === 'billing' && 'Manage invoices and parent billing'}
            {activeView === 'payout' && 'Track and manage tutor payouts'}
            {activeView === 'users' && 'Manage users and registration status'}
            {activeView === 'subjects' && 'Manage subjects and topics'}
            {activeView === 'availability' && 'View tutor weekly availability schedules'}
            {activeView === 'profile' && 'View and update your profile information'}
            {activeView === 'messages' && 'Communicate with parents, tutors, and students'}
          </p>
        </div>

        {/* Stats Grid - Show only on overview */}
        {activeView === 'overview' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{stat.value}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{stat.change}</p>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Dynamic Content Based on Active View */}
        {activeView === 'overview' && <OverviewContent />}
        {activeView === 'tasks' && <TaskManagement 
          key="task-management"
          tasks={tasks}
          setTasks={setTasks}
          taskSearchTerm={taskSearchTerm}
          handleTaskSearchChange={handleTaskSearchChange}
          taskModalOpen={taskModalOpen}
          setTaskModalOpen={setTaskModalOpen}
          editingTask={editingTask}
          setEditingTask={setEditingTask}
          fetchTasks={fetchTasks}
        />}
        {activeView === 'timesheets' && <TimesheetManagement />}
        {activeView === 'billing' && <BillingManagement />}
        {activeView === 'availability' && (
          <div className="space-y-6">
            {/* Tutor Selector Dropdown */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-600" />
                Select Tutor
              </h2>
              <div className="relative max-w-md">
                <button
                  onClick={() => setAvailabilityDropdownOpen(!availabilityDropdownOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-left hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <span className={`${selectedTutorForAvailability ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                    {selectedTutorForAvailability 
                      ? tutors.find(t => t.id === selectedTutorForAvailability)?.full_name || 'Select a tutor'
                      : 'Select a tutor to view availability'}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${availabilityDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {availabilityDropdownOpen && (
                  <div className="absolute z-10 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                    {tutors.length === 0 ? (
                      <div className="px-4 py-3 text-gray-500 dark:text-gray-400 text-center">
                        No tutors found
                      </div>
                    ) : (
                      tutors.map(tutor => (
                        <button
                          key={tutor.id}
                          onClick={() => {
                            setSelectedTutorForAvailability(tutor.id);
                            setAvailabilityDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 ${
                            selectedTutorForAvailability === tutor.id ? 'bg-cyan-50 dark:bg-cyan-900/20' : ''
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                            {tutor.full_name?.charAt(0) || 'T'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{tutor.full_name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{tutor.email}</p>
                          </div>
                          {selectedTutorForAvailability === tutor.id && (
                            <CheckCircle className="w-5 h-5 text-cyan-600 ml-auto" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Tutor Availability Card View */}
            {selectedTutorForAvailability ? (
              <TutorAvailabilityCardView key={selectedTutorForAvailability} tutorId={selectedTutorForAvailability} />
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 shadow-lg text-center">
                <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Tutor Selected</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Select a tutor from the dropdown above to view their weekly availability schedule.
                </p>
              </div>
            )}
          </div>
        )}
        {activeView === 'payout' && <PayoutManagement />}
        {activeView === 'invoices' && <InvoiceManagement />}
        {activeView === 'subjects' && <SubjectsManagement />}
        {activeView === 'users' && <UsersManagement />}
        {activeView === 'profile' && <ProfileView />}
        {activeView === 'messages' && <MessagesPage />}
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;